import type { AxiosError } from 'axios';
import { ErrorCategory } from '../types/retry';
import type { RetryStrategy, TokenRefreshConfig } from '../types/config';
import { classifyError } from './errorClassifier';

/**
 * Get the retry strategy for a given error.
 *
 * Classifies the error and returns the appropriate retry strategy
 * from the configuration based on the error category.
 *
 * @param error The axios error
 * @param config The token refresh configuration
 * @returns The retry strategy to use
 *
 * @example
 * ```typescript
 * const strategy = getRetryStrategyForError(axiosError, config);
 * if (strategy.enabled) {
 *   // Perform retry with strategy.maxAttempts and strategy.delays
 * }
 * ```
 */
export function getRetryStrategyForError(
  error: AxiosError,
  config: TokenRefreshConfig
): RetryStrategy {
  const category = classifyError(error, config.customErrorClassifier);

  switch (category) {
    case ErrorCategory.ColdStart:
      return config.retryStrategies.coldStart;
    case ErrorCategory.Timeout:
      return config.retryStrategies.timeout;
    case ErrorCategory.NetworkError:
      return config.retryStrategies.networkError;
    case ErrorCategory.PermissionRefresh:
      return config.retryStrategies.permissionRefresh;
    case ErrorCategory.AuthFailure:
      return config.retryStrategies.authFailure;
    case ErrorCategory.Unknown:
    default:
      return config.retryStrategies.unknown;
  }
}
